/**
 * Step-up authentication coordinator (ADR-014 D6).
 *
 * The auth service gates five sensitive endpoints behind `require_recent_auth`,
 * which returns 401 with a structured detail:
 *
 *     { "detail": { "error": "step_up_required", "max_age_minutes": 5, ... } }
 *
 * The axios interceptors in `src/api/client.ts` (platform API) and
 * `src/api/twofa.ts` (auth API) call `requestStepUp()` when they see that
 * response shape. This module:
 *
 *   1. Bridges the interceptor's async retry to a React modal that asks
 *      the user to re-enter their password.
 *   2. Deduplicates concurrent step-up demands (multiple in-flight sensitive
 *      requests trigger one modal, not N).
 *   3. Exposes imperative `fulfillStepUp(token)` / `rejectStepUp()` so the
 *      modal can resolve/abort the waiting requests.
 *
 * Usage:
 *   - Interceptor: `const newToken = await requestStepUp();` then retry.
 *   - Modal on mount: `registerStepUpOpener(() => setOpen(true))`.
 *   - Modal on confirm-success: `fulfillStepUp(accessToken)`.
 *   - Modal on cancel / close: `rejectStepUp(new Error('cancelled'))`.
 */

// ---------------------------------------------------------------------------
// State (module-level singleton)
// ---------------------------------------------------------------------------

interface PendingStepUp {
  resolvers: Array<(token: string) => void>;
  rejecters: Array<(err: Error) => void>;
}

let pending: PendingStepUp | null = null;
let openModal: (() => void) | null = null;

/**
 * The user declined the password prompt.
 *
 * A distinct type, because "the user chose to stop" and "this failed" need
 * opposite treatment and the interceptor cannot tell them apart otherwise. It used
 * to re-reject the ORIGINAL 401 in both cases, so a caller that cancelled the
 * prompt got the step-up demand rendered as a red error — telling them to do the
 * thing they had just declined. Reported by the owner on the 2FA setup dialog,
 * 2026-08-01, where it surfaced as the raw code `step_up_required`.
 *
 * Callers use `isStepUpCancelled(e)` and return silently. A caller that forgets
 * still degrades safely: this is not an `ApiError`, so the usual
 * `e instanceof ApiError ? translateApiError(...) : t('feedback.actionFailed')`
 * branch shows a generic message rather than a misleading instruction.
 */
export class StepUpCancelledError extends Error {
  constructor() {
    super('step_up_cancelled');
    this.name = 'StepUpCancelledError';
  }
}

/** True when this rejection is the user declining the password prompt. */
export function isStepUpCancelled(e: unknown): boolean {
  return e instanceof StepUpCancelledError;
}

// ---------------------------------------------------------------------------
// Interceptor-facing API
// ---------------------------------------------------------------------------

/**
 * Ask the user to prove they are still at the keyboard. Resolves with a
 * fresh access_token once the user confirms their password, or rejects if
 * the user cancels the modal.
 *
 * Concurrent calls share the same pending session — only one modal opens.
 */
export function requestStepUp(): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    if (pending) {
      // A step-up is already in flight; piggy-back on it.
      pending.resolvers.push(resolve);
      pending.rejecters.push(reject);
      return;
    }
    pending = {
      resolvers: [resolve],
      rejecters: [reject],
    };
    if (openModal) {
      openModal();
    } else {
      // No modal is mounted. Fail fast so the interceptor doesn't hang forever.
      //
      // The comment this replaces read "shouldn't happen in Admin, but
      // defensively safe". In Admin it happened on EVERY step-up demand until
      // 2026-07-31, because `<StepUpModal />` was rendered nowhere — so 2FA
      // enrolment was impossible on all four entry points. A defensive branch
      // that turns out to be the only branch taken is a bug report nobody read.
      //
      // Reaching here now means the mount was removed, which
      // src/api/stepUpModalMounted.test.ts exists to make impossible.
      const waiters = pending;
      pending = null;
      waiters.rejecters.forEach((r) =>
        r(new Error('StepUpModal is not mounted')),
      );
    }
  });
}

// ---------------------------------------------------------------------------
// Modal-facing API
// ---------------------------------------------------------------------------

/**
 * Register the modal's opener. Returns an unsubscribe function for cleanup
 * so tests (or hot-reload) can replace the handler cleanly.
 */
export function registerStepUpOpener(open: () => void): () => void {
  openModal = open;
  return () => {
    if (openModal === open) openModal = null;
  };
}

/** Called by the modal on successful /reauthenticate. */
export function fulfillStepUp(accessToken: string): void {
  if (!pending) return;
  const { resolvers } = pending;
  pending = null;
  resolvers.forEach((r) => r(accessToken));
}

/** Called by the modal on user cancel or non-retryable error. */
export function rejectStepUp(err: Error): void {
  if (!pending) return;
  const { rejecters } = pending;
  pending = null;
  rejecters.forEach((r) => r(err));
}

/** Introspection for tests / devtools. */
export function isStepUpPending(): boolean {
  return pending !== null;
}
